import React, { FC, ReactNode, useMemo } from 'react'
import styled from 'styled-components'

const INK = '#212121'
const MUTED = '#555555'
const FAINT = '#B0B0B0'
const BORDER = '#EAECF0'
const BLUE = '#B0020B'
const BLUE_BG = '#F9F9F9'

const SectionHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin: 4px 0 12px;
`
const SectionKicker = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: ${FAINT};
  text-transform: uppercase;
`
const SectionName = styled.h2`
  font-size: 15px;
  font-weight: 680;
  color: ${INK};
  margin: 0;
`
const SectionNote = styled.span`
  margin-left: auto;
  font-size: 11.5px;
  color: ${FAINT};
`
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`
const Card = styled.div`
  display: flex;
  gap: 12px;
  background: #fff;
  border: 1px solid ${BORDER};
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  padding: 16px;
`
const Icon = styled.div<{ $tone: 'blue' | 'green' }>`
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  color: ${BLUE};
  background: ${BLUE_BG};
`
const Body = styled.div`
  min-width: 0;
`
const Kicker = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${FAINT};
  margin-bottom: 4px;
`
const Text = styled.p`
  font-size: 13px;
  line-height: 1.45;
  color: ${MUTED};
  margin: 0;
  b {
    color: ${INK};
    font-weight: 680;
  }
`

export interface InsightsInput {
  baseName: string
  compareName: string
  baseCum: number[]
  compareCum: number[]
  baseTotal: number
  compareTotal: number
  baseTicket: number
  compareTicket: number
  baseRecPct: number
  compareRecPct: number
}

interface InsightCard {
  kicker: string
  tone: 'blue' | 'green'
  node: ReactNode
}

const fmtBRL2 = (n: number) =>
  'R$ ' + n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const at = (arr: number[], i: number) =>
  arr.length === 0 ? 0 : arr[Math.min(i, arr.length - 1)]

/** Leituras automáticas por regra sobre os números da janela. OP-3414 Fase 2. */
function buildInsights(i: InsightsInput): InsightCard[] {
  const cards: InsightCard[] = []

  // 1) Volume nos primeiros 15 dias
  const b15 = at(i.baseCum, 15)
  const c15 = at(i.compareCum, 15)
  if (b15 > 0 || c15 > 0) {
    const diff = b15 > 0 ? ((c15 - b15) / b15) * 100 : 0
    const up = c15 >= b15
    cards.push({
      kicker: 'Volume · primeiros 15 dias',
      tone: up ? 'green' : 'blue',
      node: (
        <>
          O <b>{i.compareName}</b> vendeu <b>{Math.abs(diff).toFixed(0)}% {up ? 'mais' : 'menos'}</b>{' '}
          ingressos que o Base nos primeiros 15 dias de venda.
        </>
      ),
    })
  }

  // 2) Ticket médio
  if (i.baseTicket > 0 && i.compareTicket > 0) {
    const diff = ((i.compareTicket - i.baseTicket) / i.baseTicket) * 100
    const up = i.compareTicket >= i.baseTicket
    cards.push({
      kicker: 'Ticket médio',
      tone: up ? 'green' : 'blue',
      node: (
        <>
          O ticket médio do Comparado foi <b>{Math.abs(diff).toFixed(0)}% {up ? 'maior' : 'menor'}</b>{' '}
          ({fmtBRL2(i.compareTicket)} vs {fmtBRL2(i.baseTicket)}).
        </>
      ),
    })
  }

  // 3) Concentração na abertura (4 primeiros dias: D+0..D+3)
  const bConc = i.baseTotal > 0 ? (at(i.baseCum, 3) / i.baseTotal) * 100 : 0
  const cConc = i.compareTotal > 0 ? (at(i.compareCum, 3) / i.compareTotal) * 100 : 0
  if (bConc > 0 || cConc > 0) {
    const baseStronger = bConc >= cConc
    cards.push({
      kicker: 'Concentração na abertura',
      tone: 'blue',
      node: (
        <>
          O <b>{i.baseName}</b> concentrou <b>{bConc.toFixed(0)}%</b> das vendas nos 4 primeiros dias,
          contra <b>{cConc.toFixed(0)}%</b> do Comparado — abertura {baseStronger ? 'mais' : 'menos'} forte.
        </>
      ),
    })
  }

  // 4) Fidelização (recorrência do produtor)
  if (i.baseRecPct > 0 || i.compareRecPct > 0) {
    const compareWins = i.compareRecPct >= i.baseRecPct
    const winner = compareWins ? i.compareName : i.baseName
    const pct = compareWins ? i.compareRecPct : i.baseRecPct
    cards.push({
      kicker: 'Fidelização',
      tone: 'green',
      node: (
        <>
          Maior recorrência de público no <b>{winner}</b> ({pct.toFixed(0)}% de compradores recorrentes
          do produtor).
        </>
      ),
    })
  }

  return cards
}

const InsightsSection: FC<{ input: InsightsInput }> = ({ input }) => {
  const cards = useMemo(() => buildInsights(input), [input])
  if (cards.length === 0) return null
  return (
    <>
      <SectionHead>
        <SectionKicker>Insights</SectionKicker>
        <SectionName>Leituras automáticas</SectionName>
        <SectionNote>Geradas por regras sobre os números do período</SectionNote>
      </SectionHead>
      <Grid>
        {cards.map((c) => (
          <Card key={c.kicker}>
            <Icon $tone={c.tone}>✦</Icon>
            <Body>
              <Kicker>{c.kicker}</Kicker>
              <Text>{c.node}</Text>
            </Body>
          </Card>
        ))}
      </Grid>
    </>
  )
}

export default InsightsSection
